import React, { useState, useEffect } from 'react';
import { UserFocus } from '@phosphor-icons/react';
import { DialogProps } from '@radix-ui/react-dialog';
import { createId } from '@paralleldrive/cuid2';

import { useConvosContext } from './convos';
import { Table, TableBody, TableCell, TableRow, TableHeader, TableHead } from '../ui/table';
import { ScrollArea } from '../ui/scroll-area';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/index';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { DivButton } from '@/components/ui/div-button';
import { sortAttributeKeys } from '@/lib/sortAttributeKeys';

import './attributes-dialog.css';

interface Props extends DialogProps {
  conversationId: string;
}

export function AttributesDialog({ conversationId, open, onOpenChange, ...args }: Props) {
  const [attributes, setAttributes] = useState<Record<string, any>[]>();
  const [sortedAttributesKeys, setSortedAttributesKeys] = useState<string[]>();

  const { fetchAttributes } = useConvosContext();

  useEffect(() => {
    void fetchAttributes(conversationId)
      .then((attrs) => {
        setAttributes(attrs);
        setSortedAttributesKeys(sortAttributeKeys(attrs));
      })
      .catch(() => {
        // Error handling - could be extended with error reporting
      });
  }, [fetchAttributes, conversationId]);

  if (!attributes?.length) {
    return null;
  }

  return (
    <Sheet modal={false} open={open} onOpenChange={onOpenChange} {...args}>
      <Tooltip>
        <TooltipTrigger asChild>
          <SheetTrigger asChild onClick={(e) => e.stopPropagation()} onKeyUp={(e) => e.stopPropagation()}>
            <DivButton
              variant="ghost"
              size="icon"
              className="size-7 [&_svg]:size-6 text-dark-900"
              aria-label="Open Custom attributes dialog"
            >
              <UserFocus size={24} />
            </DivButton>
          </SheetTrigger>
        </TooltipTrigger>
        <TooltipContent>Customer attributes</TooltipContent>
      </Tooltip>
      <SheetContent className="space-y-4">
        <SheetHeader>
          <SheetTitle className="heading-sm m-0">Custom attributes</SheetTitle>
          <SheetDescription className="sr-only">
            Custom attributes are used to store additional information about the conversation.
          </SheetDescription>
        </SheetHeader>
        {sortedAttributesKeys && (
          <ScrollArea className="h-[calc(100vh-8rem)]">
            {' '}
            {/* 8rem is an approximation, accounting for header, padding, and close button */}
            <Table className="bg-surface" data-test-id="attributes-table">
              <TableHeader>
                <TableRow>
                  {sortedAttributesKeys.map((key) => (
                    <TableHead key={key} className="capitalize font-bold">
                      {key}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {attributes.map((record) => (
                  <TableRow key={record?.id ?? createId()} className="border-none">
                    {sortedAttributesKeys.map((key) => (
                      <TableCell key={key} data-title={key}>
                        {typeof record[key] === 'boolean' ? String(record[key]) : record[key]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </SheetContent>
    </Sheet>
  );
}
